import { apiDeleteV2, apiGetV2, apiPatchV2, apiPostV2, type QueryParams } from "~/shared/api/client";
import { toSnakeCase } from "~/shared/lib/backend-mapper";

import type {
    AttachmentCreate,
    AttachmentKind,
    AttachmentListResponse,
    AttachmentStatus,
    Chat,
    ChatListResponse,
    ChatMessage,
    ChatRegistryResponse,
    ChatRegistryTab,
    GeneratedArtifactsListResponse,
    MessageListResponse,
    MessageRole,
    DeleteMessagesPayload,
    DeleteMessagesResponse,
    MoveMessagesPayload,
    MoveMessagesResponse,
} from "./types";

/** Query-параметры листинга чатов: скоуп (воркспейс/проект/сцена), лимит и поиск по заголовку. */
export interface ListChatsParams {
    workspaceId?: string;
    projectId?: string;
    sceneId?: string;
    limit?: number;
    includeArchived?: boolean;
    // Server-side ILIKE по title — case-insensitive substring match.
    // Бэк ограничивает 1..100 символов; gating <3 — на стороне фронта.
    search?: string;
}

/** Получить страницу чатов по фильтрам {@link ListChatsParams} — GET `/v2/chats`. */
export async function listChats(params: ListChatsParams = {}): Promise<ChatListResponse> {
    const query: QueryParams = {};
    if (params.workspaceId) query.workspaceId = params.workspaceId;
    if (params.projectId) query.projectId = params.projectId;
    if (params.sceneId) query.sceneId = params.sceneId;
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.includeArchived) query.includeArchived = true;
    if (params.search) query.search = params.search;
    return apiGetV2<ChatListResponse>("/chats", query);
}

/** Query-параметры реестра чатов админки: таб, пагинация, сортировка, поиск и диапазоны дат. */
export interface ListChatsRegistryParams {
    workspaceId?: string;
    tab?: ChatRegistryTab;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    search?: string;
    projectId?: string;
    sceneId?: string;
    /** Creator filter — honoured by the backend only for workspace managers. */
    creatorId?: string;
    createdFrom?: string;
    createdTo?: string;
    updatedFrom?: string;
    updatedTo?: string;
}

function buildChatRegistryQuery(params: ListChatsRegistryParams): string {
    const usp = new URLSearchParams();
    if (params.workspaceId) usp.set("workspace_id", params.workspaceId);
    if (params.tab) usp.set("tab", params.tab);
    if (params.search) usp.set("search", params.search);
    if (params.sortBy) usp.set("sort_by", toSnakeCase(params.sortBy));
    if (params.sortDir) usp.set("sort_dir", params.sortDir);
    if (params.page) usp.set("page", String(params.page));
    if (params.pageSize) usp.set("page_size", String(params.pageSize));
    if (params.projectId) usp.set("project_id", params.projectId);
    if (params.sceneId) usp.set("scene_id", params.sceneId);
    if (params.creatorId) usp.set("creator_id", params.creatorId);
    if (params.createdFrom) usp.set("created_from", params.createdFrom);
    if (params.createdTo) usp.set("created_to", params.createdTo);
    if (params.updatedFrom) usp.set("updated_from", params.updatedFrom);
    if (params.updatedTo) usp.set("updated_to", params.updatedTo);
    const qs = usp.toString();
    return qs ? `?${qs}` : "";
}

/** Получить страницу реестра чатов для админ-таба — GET `/v2/chats/registry`. */
export async function listChatsRegistry(params: ListChatsRegistryParams = {}): Promise<ChatRegistryResponse> {
    return apiGetV2<ChatRegistryResponse>(`/chats/registry${buildChatRegistryQuery(params)}`);
}

/** Создать чат в воркспейсе (опционально сразу в проекте/сцене) — POST `/v2/chats`. */
export async function createChat(payload: {
    workspaceId: string;
    projectId?: string;
    sceneId?: string;
    title?: string;
}): Promise<Chat> {
    return apiPostV2<Chat>("/chats", payload);
}

/** Получить чат по id — GET `/v2/chats/{chatId}`. */
export async function getChat(chatId: string): Promise<Chat> {
    return apiGetV2<Chat>(`/chats/${chatId}`);
}

/** Переименовать чат — PATCH `/v2/chats/{chatId}`. */
export async function renameChat(chatId: string, title: string): Promise<Chat> {
    return apiPatchV2<Chat>(`/chats/${chatId}`, { title });
}

/** Ответ генерации заголовка чата; `fallback: true` — заголовок собран эвристикой, LLM была недоступна. */
export interface ChatTitleResponse {
    title: string;
    fallback: boolean;
}

/** Сгенерировать заголовок чата по первому сообщению — POST `/v2/chat-title`. */
export async function generateChatTitle(message: string): Promise<ChatTitleResponse> {
    return apiPostV2<ChatTitleResponse>("/chat-title", { message });
}

/** Архивировать чат — POST `/v2/chats/{chatId}/archive`. */
export async function archiveChat(chatId: string): Promise<Chat> {
    return apiPostV2<Chat>(`/chats/${chatId}/archive`);
}

/** Закрепить чат в списке — POST `/v2/chats/{chatId}/pin`. */
export async function pinChat(chatId: string): Promise<Chat> {
    return apiPostV2<Chat>(`/chats/${chatId}/pin`);
}

/** Открепить чат — POST `/v2/chats/{chatId}/unpin`. */
export async function unpinChat(chatId: string): Promise<Chat> {
    return apiPostV2<Chat>(`/chats/${chatId}/unpin`);
}

/** Вернуть чат из архива — POST `/v2/chats/{chatId}/unarchive`. */
export async function unarchiveChat(chatId: string): Promise<Chat> {
    return apiPostV2<Chat>(`/chats/${chatId}/unarchive`);
}

/** Мягко удалить чат (только владелец) — DELETE `/v2/chats/{chatId}`, бэкенд возвращает soft-deleted чат. */
export async function deleteChat(chatId: string): Promise<Chat> {
    return apiDeleteV2<Chat>(`/chats/${chatId}`);
}

/** Payload переноса чата: обязателен целевой воркспейс, проект/сцена — опциональны. */
export interface MoveChatPayload {
    // Target workspace is required (defaults to the chat's current one on the UI).
    targetWorkspaceId: string;
    // Optional: omit project → chat sits at workspace level; scene requires a project.
    targetProjectId?: string;
    targetSceneId?: string;
}

/** Перенести чат в другой воркспейс/проект/сцену одним вызовом (только владелец) — POST `/v2/chats/{chatId}/move`. */
export async function moveChat(chatId: string, payload: MoveChatPayload): Promise<Chat> {
    return apiPostV2<Chat>(`/chats/${chatId}/move`, payload);
}

/**
 * Перенести импортированные сообщения чата в другой чат того же воркспейса —
 * в существующий (`targetChatId`) либо в новый, созданный на лету (`newChatTitle`).
 * Гейты доступа (суперадмин + «сообщения действительно импортированы») — на
 * бэкенде; `apiPostV2` сам маппит camelCase-payload в snake_case.
 */
export async function moveMessages(
    chatId: string,
    payload: MoveMessagesPayload,
): Promise<MoveMessagesResponse> {
    return apiPostV2<MoveMessagesResponse>(`/chats/${chatId}/messages/move`, payload);
}

/**
 * Безвозвратно удалить импортированные сообщения чата (только суперадмин):
 * чистит БД-строки, S3-объекты (с учётом reference-counting) и делает
 * billing-возврат за них.
 */
export async function deleteMessages(
    chatId: string,
    payload: DeleteMessagesPayload,
): Promise<DeleteMessagesResponse> {
    return apiPostV2<DeleteMessagesResponse>(`/chats/${chatId}/messages/delete`, payload);
}

/** Получить страницу сообщений чата (курсор `before`, сортировка `order`, поиск по тексту) — GET `/v2/chats/{chatId}/messages`. */
export async function listMessages(
    chatId: string,
    params: { limit?: number; before?: string; order?: "asc" | "desc"; search?: string } = {},
): Promise<MessageListResponse> {
    const query: QueryParams = {};
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.before) query.before = params.before;
    if (params.order) query.order = params.order;
    if (params.search) query.search = params.search;
    return apiGetV2<MessageListResponse>(`/chats/${chatId}/messages`, query);
}

/** Отправить сообщение в чат (с вложениями) — POST `/v2/chats/{chatId}/messages`. */
export async function postMessage(
    chatId: string,
    payload: {
        prompt: string;
        role: MessageRole;
        negativePrompt?: string;
        modelName?: string;
        attachments?: AttachmentCreate[];
    },
): Promise<ChatMessage> {
    return apiPostV2<ChatMessage>(`/chats/${chatId}/messages`, payload);
}

/** Query-параметры листинга вложений чата: фильтр по типу/статусу, курсорная пагинация. */
export interface ListChatAttachmentsParams {
    kind?: AttachmentKind;
    status?: AttachmentStatus;
    limit?: number;
    /** Взаимоисключающие курсоры по id вложения: соседняя страница старше
     *  (`before`) или новее (`after`). Ими лента дочитывается на любую длину —
     *  сервер ограничивает лишь размер страницы. */
    before?: string;
    after?: string;
}

/** Получить страницу вложений чата — GET `/v2/chats/{chatId}/attachments`. */
export async function listChatAttachments(
    chatId: string,
    params: ListChatAttachmentsParams = {},
): Promise<AttachmentListResponse> {
    const query: QueryParams = {};
    if (params.kind) query.kind = params.kind;
    if (params.status) query.status = params.status;
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.before) query.before = params.before;
    if (params.after) query.after = params.after;
    return apiGetV2<AttachmentListResponse>(`/chats/${chatId}/attachments`, query);
}

/** Query-параметры листинга сгенерированных артефактов чата: окно вокруг `anchor` либо курсорная пагинация. */
export interface ListChatGeneratedArtifactsParams {
    type?: AttachmentKind;
    limit?: number;
    /** Взаимоисключающие курсоры по id артефакта: окно вокруг него (`anchor`) либо
     *  соседняя страница старше / новее (`before` / `after`). Ими лента чата
     *  дочитывается на любую длину — сервер ограничивает лишь размер страницы. */
    anchor?: string;
    before?: string;
    after?: string;
}

/** Получить страницу сгенерированных артефактов чата — GET `/artifacts/by-chat`. */
export async function listChatGeneratedArtifacts(
    chatUuid: string,
    params: ListChatGeneratedArtifactsParams = {},
): Promise<GeneratedArtifactsListResponse> {
    const query: QueryParams = { chatUuid };
    if (params.type) query.type = params.type;
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.anchor) query.anchor = params.anchor;
    if (params.before) query.before = params.before;
    if (params.after) query.after = params.after;
    return apiGetV2<GeneratedArtifactsListResponse>("/artifacts/by-chat", query);
}
